/**
 * Reseller email history — Pass D step 15.
 *
 * Lists every customer-facing email this reseller's bookings have
 * generated (confirmations, reminders, action-needed, etc.). The
 * reseller doesn't get CC'd on these emails (Q16) — this page is
 * their full-fidelity audit trail.
 */
import { useEffect, useState } from 'react'
import ResellerShell from './ResellerShell'
import { tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'

export default function EmailHistory() {
  const { t } = useLanguage()
  const [emails, setEmails] = useState(null)
  const [selected, setSelected] = useState(null)
  const [err, setErr] = useState(null)

  useEffect(() => {
    tournamentApi.get('/portal/reseller/email-history')
      .then(r => setEmails(r.data?.emails || []))
      .catch(e => setErr(e?.response?.data?.message || e.message))
  }, [])

  function openDetail(id) {
    tournamentApi.get('/portal/reseller/email-history/' + id)
      .then(r => setSelected(r.data?.email || null))
      .catch(e => setErr(e?.response?.data?.message || e.message))
  }

  return (
    <ResellerShell title={t('sp_email_history', 'Email history')}>
      <p style={{ color: '#64748b', fontSize: 13 }}>
        {t('sp_email_history_intro', 'Every email sent on your behalf to customers. Transactional emails (confirmation, payment receipt, refund) are always sent; marketing emails (reminders, review request) respect customer unsubscribes.')}
      </p>
      {err && <div style={errorBox}>{err}</div>}
      {!emails ? <div>{t('sp_loading_short', 'Loading…')}</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 600, overflowY: 'auto' }}>
            {emails.length === 0 && <div style={{ color: '#64748b', fontSize: 13 }}>{t('sp_no_emails_yet', 'No emails yet.')}</div>}
            {emails.map(e => (
              <button
                key={e.ID_NO}
                type="button"
                onClick={() => openDetail(e.ID_NO)}
                style={{
                  textAlign: 'left',
                  background: selected?.ID_NO === e.ID_NO ? '#f0fdfa' : '#fff',
                  border: '1px solid ' + (selected?.ID_NO === e.ID_NO ? '#0d9488' : '#e2e8f0'),
                  borderRadius: 6,
                  padding: 10,
                  cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                <div style={{ fontWeight: 600 }}>{e.Subject}</div>
                <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
                  {t('sp_email_to_prefix', 'to {recipient}', { recipient: e.Recipient })} · {new Date(e.SentAt).toLocaleString()}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                  <Pill text={emailStatusLabel(t, e.Status)} kind={e.Status === 'sent' ? 'green' : e.Status === 'failed' ? 'red' : 'gray'} />
                  {e.IsTransactional ? <Pill text={t('sp_pill_transactional', 'transactional')} kind="blue" /> : <Pill text={t('sp_pill_marketing', 'marketing')} kind="amber" />}
                  {e.TemplateKey && <Pill text={e.TemplateKey} kind="gray" />}
                </div>
              </button>
            ))}
          </div>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, maxHeight: 600, overflowY: 'auto' }}>
            {!selected ? (
              <div style={{ color: '#94a3b8', fontSize: 13 }}>{t('sp_select_email_prompt', 'Select an email to see the full body.')}</div>
            ) : (
              <>
                <div style={{ fontSize: 13, color: '#64748b' }}>{t('sp_email_to_label', 'To: {recipient}', { recipient: selected.Recipient })}</div>
                <div style={{ fontSize: 13, color: '#64748b' }}>{t('sp_email_sent_label', 'Sent: {date}', { date: new Date(selected.SentAt).toLocaleString() })}</div>
                <h2 style={{ fontSize: 16, marginTop: 8, marginBottom: 12 }}>{selected.Subject}</h2>
                {selected.ErrorMessage && (
                  <div style={{ ...errorBox, marginBottom: 12 }}>{selected.ErrorMessage}</div>
                )}
                <div
                  style={{ border: '1px solid #f1f5f9', borderRadius: 6, padding: 12, fontSize: 13 }}
                  dangerouslySetInnerHTML={{ __html: selected.BodyHtml || '' }}
                />
              </>
            )}
          </div>
        </div>
      )}
    </ResellerShell>
  )
}

function emailStatusLabel(t, status) {
  const map = {
    sent: t('sp_email_status_sent', 'sent'),
    failed: t('sp_email_status_failed', 'failed'),
    pending: t('sp_email_status_pending', 'pending'),
    queued: t('sp_email_status_queued', 'queued'),
  }
  return map[status] || status
}

function Pill({ text, kind }) {
  const colors = {
    green: { bg: '#d1fae5', fg: '#065f46' },
    red:   { bg: '#fee2e2', fg: '#991b1b' },
    blue:  { bg: '#dbeafe', fg: '#1e40af' },
    amber: { bg: '#fef3c7', fg: '#92400e' },
    gray:  { bg: '#e2e8f0', fg: '#475569' },
  }[kind] || { bg: '#e2e8f0', fg: '#475569' }
  return <span style={{ display: 'inline-block', padding: '2px 6px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: colors.bg, color: colors.fg }}>{text}</span>
}

const errorBox = { padding: 10, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, fontSize: 13 }
